import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useRole, type RoleType } from '../contexts/RoleContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const getRoleLabel = (role: RoleType): string => {
  switch (role) {
    case 'planner':
      return 'Planner';
    case 'executor':
      return 'Executor';
    case 'beneficiary':
      return 'Beneficiary';
    default:
      return 'Planner';
  }
};

const getRoleSubtitle = (role: RoleType, ownerName?: string): string => {
  switch (role) {
    case 'planner':
      return 'Managing Your Estate';
    case 'executor':
      return ownerName ? `for ${ownerName}'s Estate` : "for Owner's Estate";
    case 'beneficiary':
      return ownerName ? `for ${ownerName}'s Estate` : "for Owner's Estate";
    default:
      return 'Managing Your Estate';
  }
};

export const RoleSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole, availableRoles, switchRole } = useRole();

  const handleRoleSwitch = (role: RoleType, estateOwnerId?: string) => {
    const selectedRole = availableRoles.find(
      r => r.role === role && r.estateOwnerId === estateOwnerId
    );

    if (selectedRole) {
      switchRole(selectedRole);

      // Navigate based on role
      if (role === 'planner') {
        navigate('/frame-140');
      } else if (role === 'executor' && estateOwnerId) {
        navigate(`/executor-plan/${estateOwnerId}/eframe-141`);
      } else if (role === 'beneficiary' && estateOwnerId) {
        navigate(`/beneficiary-coming-soon/${estateOwnerId}`);
      }
    }
  };

  const currentRoleLabel = activeRole ? getRoleLabel(activeRole.role) : 'Planner';
  
  // For Planner, always show "Planner", not the estate owner name
  const displayName = activeRole?.role === 'planner' 
    ? 'Planner' 
    : (activeRole?.estateOwnerName || currentRoleLabel);

  return (
    // modal={false} avoids Radix scroll lock / scrollbar compensation on body, which shifts
    // fixed controls (e.g. mobile nav) and the viewport on iOS when the menu opens.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 max-w-full items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          aria-label="Account and role menu"
          data-tour="planner-role-switcher"
        >
          <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-gray-900">
            {displayName}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64 max-h-[min(70dvh,24rem)] overflow-y-auto">
        <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
        <DropdownMenuGroup>
          {availableRoles.map((role, index) => {
            const isActive =
              activeRole?.role === role.role &&
              activeRole?.estateOwnerId === role.estateOwnerId;
            const roleLabel = getRoleLabel(role.role);
            const subtitle = getRoleSubtitle(role.role, role.estateOwnerName);

            return (
              <DropdownMenuItem
                key={`${role.role}-${role.estateOwnerId || index}`}
                onClick={() => handleRoleSwitch(role.role, role.estateOwnerId)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{roleLabel}</span>
                    <span className="text-xs text-gray-500">
                      {subtitle}
                    </span>
                  </div>
                  {isActive && <Check className="h-4 w-4 text-blue-600" />}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
